import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, adminAuth } from "@/lib/api";
import { toast } from "sonner";

export default function AdminLogin() {
    const nav = useNavigate();
    const [phase, setPhase] = useState("email"); // email | password | setup
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (adminAuth.getToken()) {
            nav("/admin");
        }
    }, [nav]);

    const submitEmail = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/check", { email });
            if (!data.exists) {
                toast.error("This email isn't on the admin list.");
                return;
            }
            setPhase(data.needs_password_setup ? "setup" : "password");
        } catch (err) {
            toast.error("Couldn't check email. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const submitLogin = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/login", { email, password });
            adminAuth.setToken(data.token);
            toast.success("Welcome back.");
            nav("/admin");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Login failed."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const submitSetup = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Use at least 8 characters.");
            return;
        }
        if (password !== confirmPassword) {
            toast.error("Passwords don't match.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/set-password", {
                email,
                password,
            });
            adminAuth.setToken(data.token);
            toast.success("Password set. Welcome.");
            nav("/admin");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't set password."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-weave-bg">
            <Header minimal />
            <main className="flex-1 flex items-center justify-center px-6 py-16">
                <div className="w-full max-w-md" data-testid="admin-login-card">
                    <p className="editorial-label text-weave-teal mb-3">
                        Admin · The Weave
                    </p>
                    <h1 className="text-3xl md:text-4xl font-light tracking-tight text-weave-ink">
                        {phase === "email" && "Sign in to admin"}
                        {phase === "password" && "Enter your password"}
                        {phase === "setup" && "First time? Set a password"}
                    </h1>
                    <p className="text-weave-slate mt-3">
                        {phase === "email" &&
                            "Use the admin email you've been given."}
                        {phase === "password" && email}
                        {phase === "setup" &&
                            "You haven't set a password yet. Choose one now."}
                    </p>

                    {phase === "email" && (
                        <form
                            onSubmit={submitEmail}
                            className="mt-10 space-y-4"
                        >
                            <div>
                                <Label className="editorial-label text-weave-slate">
                                    Email
                                </Label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@wearetheweave.co.uk"
                                    required
                                    className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                                    data-testid="admin-email-input"
                                    autoFocus
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn-primary w-full"
                                data-testid="admin-email-submit"
                            >
                                {submitting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>
                                        Continue <ArrowRight className="w-4 h-4" />
                                    </>
                                )}
                            </button>
                        </form>
                    )}

                    {phase === "password" && (
                        <form onSubmit={submitLogin} className="mt-10 space-y-4">
                            <div>
                                <Label className="editorial-label text-weave-slate">
                                    Password
                                </Label>
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                    required
                                    className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                                    data-testid="admin-password-input"
                                    autoFocus
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn-primary w-full"
                                data-testid="admin-login-submit"
                            >
                                {submitting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    "Sign in"
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setPhase("email");
                                    setPassword("");
                                }}
                                className="btn-ghost w-full justify-center"
                                data-testid="admin-back-btn"
                            >
                                ← Use a different email
                            </button>
                        </form>
                    )}

                    {phase === "setup" && (
                        <form onSubmit={submitSetup} className="mt-10 space-y-4">
                            <div>
                                <Label className="editorial-label text-weave-slate">
                                    New password (min 8 chars)
                                </Label>
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                    required
                                    className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                                    data-testid="admin-new-password-input"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <Label className="editorial-label text-weave-slate">
                                    Confirm password
                                </Label>
                                <Input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) =>
                                        setConfirmPassword(e.target.value)
                                    }
                                    required
                                    className="mt-2 bg-weave-surface focus-visible:ring-weave-teal"
                                    data-testid="admin-confirm-password-input"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn-primary w-full"
                                data-testid="admin-setup-submit"
                            >
                                {submitting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    "Set password & sign in"
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </main>
        </div>
    );
}
